import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import {
  actions,
  BetHistoryItemProvider,
  BetHistoryProvider,
  constants,
  EventsProvider,
  getEventsIdsFromBets,
  SiteConfigManager,
  useBetHistory,
  useBetHistoryDispatch,
  useEventsContext,
  useGlobalBetslipState,
} from '@atlas/core';
import {
  BetHistoryItemHeader,
  BetHistoryItemHeaderShort,
  Button,
  EmptyStateView,
  Loader,
} from '@atlas/ui';

import SeoPageText from 'components/SeoPage/SeoPageText';
import ShortFooter from 'src/components/Footer/ShortFooter';
import checkIsIframe from 'src/helpers/checkIsIframe';

import BetHistoryItemCompact from './BetHistoryItemCompact';

const BetHistoryFilterC = lazy(
  () => import(/* webpackChunkName: "BetHistory" */ './BetHistoryFilter'),
);

const SuspenseComponent = Component =>
  function (props) {
    return (
      <Suspense fallback={<div />}>
        <Component {...props} />
      </Suspense>
    );
  };

const customBetHistoryOptions = {
  filter: {
    betStatuses: [constants.BET_STATUS.UNSETTLED],
  },
};

const ConnectedBetHistory = ({ iframeHeader }) => {
  const betHistoryFiltersType = React.useMemo(
    () => SiteConfigManager.getConfig('config.config.betHistory.filtersType'),
    [],
  );
  const options = betHistoryFiltersType === 'simple' && customBetHistoryOptions;

  return (
    <BetHistoryProvider options={options}>
      <BetHistoryView filtersType={betHistoryFiltersType} iframeHeader={iframeHeader} />
    </BetHistoryProvider>
  );
};

const BetHistoryItem = ({ bet, isLast, isShortViewType, onHeightChange, settings }) => {
  const [isExpanded, toggleExpanded] = useState(!isShortViewType);
  const { events: eventsData } = useEventsContext();
  const itemRef = useRef();

  // Measure height and notify parent when it changes
  useEffect(() => {
    if (isLast && itemRef.current && onHeightChange) {
      const height = itemRef.current.offsetHeight;
      onHeightChange(height);
    }
  }, [isLast, onHeightChange, isExpanded]);

  return (
    <div className="bet-order" ref={isLast ? itemRef : null}>
      <BetHistoryItemProvider bet={bet}>
        {isShortViewType ? (
          <BetHistoryItemHeaderShort isExpanded={isExpanded} toggleExpanded={toggleExpanded} />
        ) : (
          <BetHistoryItemHeader />
        )}

        {isExpanded && (
          <BetHistoryItemCompact
            cashoutDisabled={!settings.cashOut}
            eventsData={eventsData}
            isHistory
            isShortViewType={isShortViewType}
            orderCashoutDisabled={!settings.orderCashOut}
            partialCashoutDisabled={!settings.partialCashOut}
            showEditButton={false}
          />
        )}
      </BetHistoryItemProvider>
    </div>
  );
};

const BetHistoryView = ({ filtersType, iframeHeader }) => {
  const scrollRef = useRef();
  const { bets, filter, loading, slice } = useBetHistory();
  const { settings } = useGlobalBetslipState();
  const [loadMoreBtnHeight, setLoadMoreBtnHeight] = useState(0);

  const isShortViewType = useMemo(
    () => SiteConfigManager.getConfig('config.config.betHistory.viewType', 'full') === 'short',
    [],
  );

  // will scroll to top if new bets were loaded
  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    scrollRef &&
      !filter.slice.currentSlice &&
      scrollRef.current &&
      scrollRef.current.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
  }, [bets]);

  const filterData = useMemo(() => {
    return {
      eventIds: getEventsIdsFromBets(bets, true),
      onlyMainMarketTypes: false,
      sportServices: [constants.SPORT_SERVICE.LIVE, constants.SPORT_SERVICE.PREMATCH],
    };
  }, [bets]);

  const dispatch = useBetHistoryDispatch();
  const BetHistoryFilter = SuspenseComponent(BetHistoryFilterC);

  const changePageHandler = React.useCallback(() => {
    const currentSlice = filter.slice?.currentSlice || 0;

    dispatch({
      payload: {
        // Switch slice to the next one
        slice: {
          currentSlice: currentSlice + 1,
        },
      },
      type: actions.FILTER_UPDATE,
    });
  }, [dispatch, filter.slice]);

  const handleLastItemHeightChange = React.useCallback(
    lastItemHeight => setLoadMoreBtnHeight(lastItemHeight),
    [],
  );

  const content = useMemo(() => {
    if (loading && !bets.length) {
      return <Loader className="relative" />;
    }

    if (!filterData.eventIds.length) {
      return (
        <Suspense fallback={<div />}>
          <EmptyStateView
            className="empty-center"
            description="bet_history_empty_message"
            title="bet_history_empty_title"
          />
        </Suspense>
      );
    }

    return (
      <>
        <EventsProvider filter={filterData}>
          <div className="bet-history-wrapper-inner">
            {bets.map(
              (bet, index) => (
                <BetHistoryItem
                  bet={bet}
                  isLast={index === bets.length - 1}
                  isShortViewType={isShortViewType}
                  key={bet.betId}
                  onHeightChange={handleLastItemHeightChange}
                  settings={settings}
                />
              ),
              [loading, bets],
            )}

            {slice && (
              <LoadMoreButton
                loading={loading}
                onClick={changePageHandler}
                overlayHeight={loadMoreBtnHeight}
                slice={slice}
              />
            )}
          </div>
        </EventsProvider>
      </>
    );
  }, [loading, filterData, loadMoreBtnHeight]);

  return (
    <>
      {iframeHeader}
      <BetHistoryFilter type={filtersType} />
      <div
        className={classNames('bet-history-profile-container', {
          'short-view': isShortViewType,
        })}
      >
        <div className="bet-history-list-container" ref={scrollRef}>
          {content}
          <SeoPageText />
          {!checkIsIframe() && <ShortFooter />}
        </div>
      </div>
    </>
  );
};

const LoadMoreButton = ({ loading, onClick, overlayHeight, slice }) => {
  if (!slice.hasNext) {
    return null;
  }

  const loadMoreStyles = {
    height: (overlayHeight > 100 ? overlayHeight : overlayHeight - 4) || 'auto', // overlayHeight - 4 => fix for short viewType
  };

  return (
    <div className="load-more" {...(!checkIsIframe() && { style: loadMoreStyles })}>
      {loading ? (
        <Loader className="list-empty" />
      ) : (
        <Button label="load_more" onClick={onClick} size="medium" type="outline" />
      )}
    </div>
  );
};

export default ConnectedBetHistory;
